import React, { useEffect, useState } from "react"
import { Loader2, Send } from "lucide-react"

import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Switch } from "src/components/ui/switch"
import { useToast } from "src/components/ui/toast"
import { PROVIDER_CATALOG } from "src/features/integrations/providerCatalog"
import { useProviderSettings } from "src/features/integrations/hooks/useProviderSettings"

// Config-driven settings card for a registry provider (exotel / msg91 /
// springedge). Which fields render comes from PROVIDER_CATALOG, so a new
// provider is a catalog entry — not a new component. Visual language matches
// GupshupCard (gradient card, status pill, enabled kill-switch, write-only
// secrets, test send).
export default function ProviderCard({ provider }) {
  const meta = PROVIDER_CATALOG[provider]
  const { config, isLoading, isSaving, isTesting, save, test } = useProviderSettings(provider)
  const { toast } = useToast()

  const [form, setForm] = useState({})
  const [testNumber, setTestNumber] = useState("")

  // Hydrate non-secret fields once settings load; secrets always start blank
  // (never echoed back — blank means "keep the stored value").
  useEffect(() => {
    if (!config) return
    const next = { enabled: !!config.enabled }
    meta.fields.forEach((f) => { next[f.key] = config[f.key] || "" })
    meta.secrets.forEach((s) => { next[s.key] = "" })
    setForm(next)
  }, [config]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))

  const handleSave = async () => {
    try {
      const payload = { enabled: form.enabled }
      meta.fields.forEach((f) => { payload[f.key] = form[f.key] })
      // Omit blank secrets so we don't overwrite stored ones.
      meta.secrets.forEach((s) => {
        if ((form[s.key] || "").trim()) payload[s.key] = form[s.key].trim()
      })
      await save(payload)
      setForm((f) => {
        const cleared = { ...f }
        meta.secrets.forEach((s) => { cleared[s.key] = "" })
        return cleared
      })
      toast({ title: `${meta.name} settings saved` })
    } catch (e) {
      toast({ variant: "destructive", title: "Couldn't save", description: errorMessage(e) })
    }
  }

  const handleTest = async () => {
    if (!testNumber.trim()) {
      toast({ variant: "destructive", title: "Enter a number to test" })
      return
    }
    try {
      await test(testNumber.trim())
      toast({
        title: `Test accepted by ${meta.name}`,
        description: `Queued to ${testNumber.trim()}. Delivery status will update automatically where supported.`,
      })
    } catch (e) {
      toast({ variant: "destructive", title: "Test failed", description: errorMessage(e) })
    }
  }

  const allSecretsPresent = meta.secrets.every((s) => !!config?.[`${s.key}_present`])
  const isConfigured = !!config?.enabled && allSecretsPresent

  return (
    <div className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br ${meta.gradient} p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 ${meta.borderHover} hover:shadow-lg hover:shadow-slate-200/50`}>
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center">
          <meta.Icon color={meta.color} size={26} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">{meta.name}</h3>
          <span className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isConfigured ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${isConfigured ? "bg-green-500" : "bg-amber-400"}`} />
            {isConfigured ? "Configured" : "Not configured"}
          </span>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{meta.description}</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
              <div>
                <Label htmlFor={`${provider}-enabled`} className="font-medium text-slate-800">Enabled</Label>
                <p className="mt-0.5 text-xs text-slate-500">{meta.enabledHint}</p>
              </div>
              <Switch
                id={`${provider}-enabled`}
                checked={!!form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              />
            </div>

            {meta.fields.map((f) => (
              <div key={f.key} className="grid gap-1.5">
                <Label htmlFor={`${provider}-${f.key}`} className="text-xs font-medium text-slate-600">{f.label}</Label>
                <Input id={`${provider}-${f.key}`} value={form[f.key] || ""} onChange={set(f.key)}
                  placeholder={f.placeholder} />
                {f.hint && <p className="text-[11px] leading-relaxed text-slate-400">{f.hint}</p>}
              </div>
            ))}

            {meta.secrets.map((s) => (
              <div key={s.key} className="grid gap-1.5">
                <Label htmlFor={`${provider}-${s.key}`} className="text-xs font-medium text-slate-600">{s.label}</Label>
                <Input id={`${provider}-${s.key}`} type="password" value={form[s.key] || ""} onChange={set(s.key)}
                  placeholder={config?.[`${s.key}_present`] ? "•••••••• (saved — type to replace)" : `Enter your ${s.label.toLowerCase()}`}
                  autoComplete="off" />
              </div>
            ))}

            <div className="flex items-center justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save settings
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label htmlFor={`${provider}-test`} className="text-xs font-medium text-slate-600">{meta.testLabel}</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input id={`${provider}-test`} value={testNumber} onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="Destination number" />
                <Button variant="outline" onClick={handleTest} disabled={isTesting}>
                  {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function errorMessage(e) {
  return e?.response?.data?.error || e?.response?.data?.errors?.[0] || "Something went wrong"
}
